import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { inr, PtSlab, ptSlabsApi } from '../../payroll.api';
import CrudTable from './CrudTable';
import { isBadNumber, num, str } from './fields';

interface Draft {
  state: string;
  gender: PtSlab['gender'];
  minGross: string;
  maxGross: string;
  monthlyAmount: string;
  februaryAmount: string;
}

/** Professional tax: the amount for an earned gross between min and max, by state and gender. */
export default function PtSlabsTab({ canEdit }: { canEdit: boolean }) {
  return (
    <Stack gap={1}>
      <Typography variant='body2' color='text.secondary'>
        The slab is picked on the month’s earned gross. Maharashtra charges a higher amount in February.
      </Typography>
      <CrudTable<PtSlab, Draft>
        entityName='PT slab'
        client={ptSlabsApi}
        canEdit={canEdit}
        sort={(a, b) => a.state.localeCompare(b.state) || a.gender.localeCompare(b.gender) || a.minGross - b.minGross}
        columns={[
          { label: 'State', render: (s) => s.state },
          { label: 'Gender', render: (s) => (s.gender === 'ANY' ? 'Any' : s.gender) },
          { label: 'Earned gross from', align: 'right', render: (s) => inr(s.minGross) },
          { label: 'Up to', align: 'right', render: (s) => (s.maxGross === null ? 'and above' : inr(s.maxGross)) },
          { label: 'PT per month', align: 'right', render: (s) => inr(s.monthlyAmount) },
          { label: 'In February', align: 'right', render: (s) => inr(s.februaryAmount) },
        ]}
        emptyDraft={() => ({ state: 'MH', gender: 'ANY', minGross: '0', maxGross: '', monthlyAmount: '0', februaryAmount: '0' })}
        toDraft={(s) => ({
          state: s.state,
          gender: s.gender,
          minGross: str(s.minGross),
          maxGross: str(s.maxGross),
          monthlyAmount: str(s.monthlyAmount),
          februaryAmount: str(s.februaryAmount),
        })}
        validate={(d) =>
          !d.state.trim() || d.minGross.trim() === '' || d.monthlyAmount.trim() === ''
            ? 'State, “from” and the monthly amount are required.'
            : [d.minGross, d.maxGross, d.monthlyAmount, d.februaryAmount].some(isBadNumber)
            ? 'Amounts must be numbers.'
            : null
        }
        toPayload={(d) => ({
          state: d.state.trim().toUpperCase(),
          gender: d.gender,
          minGross: num(d.minGross),
          maxGross: num(d.maxGross),
          monthlyAmount: num(d.monthlyAmount),
          februaryAmount: num(d.februaryAmount) ?? num(d.monthlyAmount),
        })}
        renderForm={(d, set) => (
          <>
            <Stack direction='row' gap={2}>
              <TextField size='small' label='State code' value={d.state} onChange={(e) => set({ state: e.target.value })} helperText='e.g. MH' fullWidth />
              <TextField select size='small' label='Gender' value={d.gender} onChange={(e) => set({ gender: e.target.value as PtSlab['gender'] })} fullWidth>
                <MenuItem value='ANY'>Any</MenuItem>
                <MenuItem value='Male'>Male</MenuItem>
                <MenuItem value='Female'>Female</MenuItem>
              </TextField>
            </Stack>
            <Stack direction='row' gap={2}>
              <TextField size='small' type='number' label='Earned gross from' value={d.minGross} onChange={(e) => set({ minGross: e.target.value })} fullWidth />
              <TextField size='small' type='number' label='Up to (blank = no limit)' value={d.maxGross} onChange={(e) => set({ maxGross: e.target.value })} fullWidth />
            </Stack>
            <Stack direction='row' gap={2}>
              <TextField size='small' type='number' label='PT per month' value={d.monthlyAmount} onChange={(e) => set({ monthlyAmount: e.target.value })} fullWidth />
              <TextField size='small' type='number' label='PT in February' value={d.februaryAmount} onChange={(e) => set({ februaryAmount: e.target.value })} fullWidth />
            </Stack>
          </>
        )}
      />
    </Stack>
  );
}
